import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getRemoteDeviceProfile,
  getRemoteTarget,
  listRemoteTargets,
} from './deviceRemoteApi';
import { claimPwaDeviceJwtFromUrl } from './pwaClaim';
import { normalizeInstalledApps } from './installedAppSelection';

export const COMMAND_RESPONSE_TYPES = {
  remote_action: 'remote_action_response',
  tv_remote_action: 'tv_remote_action_response',
  launch_app: 'launch_app_response',
  launch_suitestream: 'launch_suitestream_response',
  list_apps: 'list_apps_response',
  wake_on_lan: 'wake_on_lan_response',
  sleep_on_lan: 'sleep_on_lan_response',
  tv_remote_connect: 'tv_remote_connect_response',
  tv_remote_disconnect: 'tv_remote_disconnect_response',
  scan_network: 'network_scan_response',
};

export const buildTargetStorageKey = (surfaceKey, remoteDeviceId) =>
  `${surfaceKey}:selectedTarget:${remoteDeviceId}`;

export const createLocalEmitter = () => {
  const responseListeners = new Set();
  const missingListeners = new Set();

  return {
    emitResponse(payload) {
      responseListeners.forEach((listener) => listener(payload));
    },
    emitMissing(payload) {
      missingListeners.forEach((listener) => listener(payload));
    },
    subscribe({ onResponse, onMissing }) {
      if (typeof onResponse === 'function') responseListeners.add(onResponse);
      if (typeof onMissing === 'function') missingListeners.add(onMissing);

      return () => {
        if (typeof onResponse === 'function') responseListeners.delete(onResponse);
        if (typeof onMissing === 'function') missingListeners.delete(onMissing);
      };
    },
  };
};

export { normalizeInstalledApps };

export const mapRemoteTargetToDevice = (target) => ({
  id: Number(target?.id || 0),
  name: target?.name || 'Unknown device',
  type: target?.type || '',
  status: target?.status || null,
  siteId: target?.siteId || target?.site_id || null,
  siteName: target?.siteName || '',
  areaId: target?.areaId || target?.area_id || null,
  areaName: target?.areaName || '',
  is_app: Boolean(target?.is_app ?? target?.isApp),
  integration_data: target?.integration_data || target?.integrationData || {},
  source: target?.source || null,
  hdmi_assignments: target?.hdmi_assignments || target?.hdmiAssignments || null,
  capabilities: target?.capabilities || {},
  appControlDeviceId: target?.appControlDeviceId || target?.app_control_device_id || null,
  appControlCapabilities:
    target?.appControlCapabilities
    || target?.app_control_capabilities
    || null,
  sourceLayout: target?.sourceLayout || null,
});

export const buildSyntheticResponse = ({ deviceId, command, value, requestId, data }) => {
  const type = COMMAND_RESPONSE_TYPES[command] || `${command}_response`;
  const payload = {
    deviceId,
    type,
    requestId,
    success: data?.success !== false,
    attempt: data?.attempt || null,
  };

  if (command === 'wake_on_lan' || command === 'sleep_on_lan') {
    payload.mac = value?.mac || null;
  }
  if (command === 'tv_remote_connect') {
    payload.status = 'connected';
    payload.message = 'LG TV connect request sent.';
  }
  if (command === 'tv_remote_disconnect') {
    payload.status = 'disconnected';
    payload.message = 'LG TV disconnect requested.';
  }

  return payload;
};

export const formatTargetOption = (target) =>
  [target?.siteName, target?.areaName, target?.name].filter(Boolean).join(' • ');

export const isAccessError = (error) => Number(error?.status || 0) === 403;

export default function useRemoteSurfaceSession({
  expectedRoute,
  targetStorageKeyPrefix,
  loadingMessage,
  errorMessage,
  emptyMessage,
}) {
  const [bootStatus, setBootStatus] = useState('loading');
  const [bootMessage, setBootMessage] = useState(loadingMessage);
  const [remoteDevice, setRemoteDevice] = useState(null);
  const [targets, setTargets] = useState([]);
  const [selectedTargetId, setSelectedTargetId] = useState('');
  const [device, setDevice] = useState(null);
  const [targetLoading, setTargetLoading] = useState(false);

  const loadTarget = useCallback(async (targetId, { silent = false } = {}) => {
    if (!targetId) {
      setDevice(null);
      setSelectedTargetId('');
      return;
    }

    if (!silent) setTargetLoading(true);
    try {
      const data = await getRemoteTarget(targetId, { includeApps: true });
      const nextDevice = mapRemoteTargetToDevice(data?.target || null);
      setDevice(nextDevice);
      setSelectedTargetId(String(nextDevice.id));
      setBootStatus('ready');
      setBootMessage('');
    } catch (error) {
      if (isAccessError(error)) {
        window.location.replace('/connect');
        return;
      }
      setBootStatus('error');
      setBootMessage(error.message || errorMessage);
    } finally {
      if (!silent) setTargetLoading(false);
    }
  }, [errorMessage]);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      try {
        await claimPwaDeviceJwtFromUrl();
      } catch (error) {
        console.warn('Unable to claim PWA remote identity', error);
      }

      const deviceJwt = localStorage.getItem('deviceJWT');
      if (!deviceJwt) {
        window.location.replace('/connect');
        return;
      }

      try {
        const [profile, targetList] = await Promise.all([
          getRemoteDeviceProfile(),
          listRemoteTargets(),
        ]);

        if (cancelled) return;

        const remote = profile?.remoteDevice || null;
        if (!remote) {
          throw new Error('Remote device profile is missing.');
        }
        if (remote.route && remote.route !== expectedRoute) {
          window.location.replace(remote.route);
          return;
        }

        const nextTargets = Array.isArray(targetList?.targets) ? targetList.targets : [];
        setRemoteDevice(remote);
        setTargets(nextTargets);

        if (!nextTargets.length) {
          setBootStatus('empty');
          setBootMessage(emptyMessage);
          return;
        }

        const storageKey = buildTargetStorageKey(targetStorageKeyPrefix, remote.id);
        const storedTargetId = localStorage.getItem(storageKey);
        const preferredTarget =
          nextTargets.find((target) => String(target.id) === String(storedTargetId || ''))
          || nextTargets[0];

        await loadTarget(preferredTarget.id);
      } catch (error) {
        if (cancelled) return;
        if (isAccessError(error)) {
          window.location.replace('/connect');
          return;
        }
        setBootStatus('error');
        setBootMessage(error.message || errorMessage);
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [emptyMessage, errorMessage, expectedRoute, loadTarget, targetStorageKeyPrefix]);

  useEffect(() => {
    if (!remoteDevice?.id || !selectedTargetId) return;
    localStorage.setItem(
      buildTargetStorageKey(targetStorageKeyPrefix, remoteDevice.id),
      String(selectedTargetId)
    );
  }, [remoteDevice?.id, selectedTargetId, targetStorageKeyPrefix]);

  const activeTarget = useMemo(
    () => targets.find((target) => String(target.id) === String(selectedTargetId)) || null,
    [selectedTargetId, targets]
  );

  const selectTarget = useCallback(async (targetId) => {
    const nextTargetId = String(targetId || '');
    setSelectedTargetId(nextTargetId);
    await loadTarget(nextTargetId);
  }, [loadTarget]);

  return {
    activeTarget,
    bootMessage,
    bootStatus,
    device,
    loadTarget,
    remoteDevice,
    selectedTargetId,
    selectTarget,
    setDevice,
    targetLoading,
    targets,
  };
}
